import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Heading,
  NativeInput,
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarLabel,
  SidebarLayout,
  SidebarSection,
  SidebarSpacer,
} from '../../components/ui'
import {
  ArrowPathRoundedSquareIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BeakerIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  Cog6ToothIcon,
  CpuChipIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  FingerPrintIcon,
  FireIcon,
  FlagIcon,
  FolderOpenIcon,
  HeartIcon,
  HomeIcon,
  MoonIcon,
  QueueListIcon,
  ScaleIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCircleIcon,
  UserGroupIcon,
  VideoCameraIcon,
} from '@heroicons/react/20/solid'
import {
  DASHBOARD_DEFAULT_ACTIVE_TAB,
  DASHBOARD_TABS,
  DASHBOARD_TAB_GROUPS,
} from './constants'
import {
  ChatTab,
  ConsultationsTab,
  EmergencyTab,
  FamilyTab,
  FitnessTab,
  GeneticsTab,
  GoalsTab,
  HealthMetricsTab,
  HomeTab,
  ImmunizationsTab,
  LongevityTab,
  MedicationsTab,
  NutritionTab,
  ProvidersTab,
  RecordsVaultTab,
  ReportsTab,
  SettingsTab,
  SleepTab,
  SupplementsTab,
  SymptomsTab,
  TestsTab,
  TimelineTab,
  WearablesTab,
} from './tabs'
import useDashboardState from './useDashboardState'
import { uiClasses } from '../shared/uiClasses'
import { cn } from '../../lib/utils'
import { themeClasses } from '../../components/shared/themeClasses'
import type { AuthMode } from '../auth/authStorage'
import type { MemberIdentity } from '../auth/types'
import type { ThemePreference } from '../theme/themePreference'
import type { DashboardIconKey, DashboardTab, DashboardTabId } from './constants'

interface DashboardPageProps {
  authMode?: AuthMode
  member: MemberIdentity
  onThemePreferenceChange: (nextPreference: ThemePreference) => void
  onBackHome: () => void
  onLogout: () => void
  themePreference: ThemePreference
}

const TAB_ICONS: Record<DashboardIconKey, typeof ChatBubbleLeftRightIcon> = {
  home: HomeIcon,
  chat: ChatBubbleLeftRightIcon,
  timeline: QueueListIcon,
  heart: HeartIcon,
  moon: MoonIcon,
  fire: FireIcon,
  scale: ScaleIcon,
  clipboard: ClipboardDocumentCheckIcon,
  chart: BoltIcon,
  test: BeakerIcon,
  folder: FolderOpenIcon,
  medication: CubeIcon,
  supplement: CubeIcon,
  shield: ShieldCheckIcon,
  sparkles: SparklesIcon,
  fingerprint: FingerPrintIcon,
  flag: FlagIcon,
  settings: Cog6ToothIcon,
  video: VideoCameraIcon,
  sync: ArrowPathRoundedSquareIcon,
  device: CpuChipIcon,
  users: UserGroupIcon,
  emergency: ExclamationTriangleIcon,
}

const USER_MENU_CONTENT_CLASS = cn(
  'dashboard-user-menu isolate w-max rounded-xl p-1 shadow-lg ring-1 dark:ring-inset',
  themeClasses.popoverSurface,
  themeClasses.popoverRing
)

const USER_MENU_ITEM_CLASS = cn(
  'group rounded-lg px-3.5 py-2.5 text-left text-base/6 forced-colors:text-[CanvasText] sm:px-3 sm:py-1.5 sm:text-sm/6',
  uiClasses.textPrimary,
  '[&_[data-slot=icon]]:mr-2.5 [&_[data-slot=icon]]:-ml-0.5 [&_[data-slot=icon]]:size-5 sm:[&_[data-slot=icon]]:mr-2 sm:[&_[data-slot=icon]]:size-4'
)

const REAL_MODE_TAB_IDS = new Set<DashboardTabId>([
  'home',
  'chat',
  'records',
  'medications',
  'reports',
  'consult',
  'providers',
  'settings',
])

function firstNameFromMemberName(name: string): string {
  return name.trim().split(' ')[0] || 'Member'
}

function initialsFromMemberName(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
}

export default function DashboardPage({
  authMode = 'mock',
  member,
  onThemePreferenceChange,
  onBackHome,
  onLogout,
  themePreference,
}: DashboardPageProps) {
  const {
    activeChatThreadId,
    activeTab,
    agentApiEnabled,
    actions,
    chatLoading,
    chatMessages,
    chatThreads,
    consultations,
    isOpenRouterConfigured,
    medications,
    metrics,
    openRouterSettings,
    personalProfile,
    providers,
    records,
    reports,
    setActiveTab,
    tasks,
  } = useDashboardState(authMode, member.email, member.name)

  const isRealMode = authMode === 'real'
  const profileDisplayName = personalProfile.preferredName.trim() || personalProfile.fullName.trim() || member.name
  const firstName = firstNameFromMemberName(profileDisplayName)
  const memberInitials = initialsFromMemberName(profileDisplayName)

  const visibleTabGroups = useMemo(() => {
    if (!isRealMode) {
      return DASHBOARD_TAB_GROUPS
    }

    return DASHBOARD_TAB_GROUPS
      .map((group) => ({
        ...group,
        tabs: group.tabs.filter((tab) => REAL_MODE_TAB_IDS.has(tab.id)),
      }))
      .filter((group) => group.tabs.length > 0)
  }, [isRealMode])

  const visibleTabs = useMemo<DashboardTab[]>(
    () => visibleTabGroups.flatMap((group) => group.tabs),
    [visibleTabGroups]
  )
  const sidebarTabGroups = useMemo(
    () =>
      visibleTabGroups
        .map((group) => ({
          ...group,
          tabs: group.tabs.filter((tab) => tab.id !== 'settings'),
        }))
        .filter((group) => group.tabs.length > 0),
    [visibleTabGroups]
  )

  useEffect(() => {
    if (!visibleTabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(DASHBOARD_DEFAULT_ACTIVE_TAB)
    }
  }, [activeTab, setActiveTab, visibleTabs])

  const activeTabMeta =
    visibleTabs.find((tab) => tab.id === activeTab) ??
    DASHBOARD_TABS.find((tab) => tab.id === activeTab)
  const hasSettingsTab = visibleTabs.some((tab) => tab.id === 'settings')

  const isHomeTab = activeTab === 'home'
  const isChatTab = activeTab === 'chat'
  const isMoreTab = !isHomeTab && !isChatTab
  const [isChatMobileSidebarOpen, setIsChatMobileSidebarOpen] = useState(false)
  const mobileNavActiveIndex = isHomeTab ? 1 : isChatTab ? 2 : 3
  const previousMobileNavIndexRef = useRef(mobileNavActiveIndex)
  const mobileNavPreviousIndex = previousMobileNavIndexRef.current
  const headerTitle = isHomeTab ? `Welcome back, ${firstName}` : (activeTabMeta?.label ?? 'Dashboard')
  const dashboardShellClassName =
    `unified-shell dashboard-view ui-dashboard-shell ui-shell landing-theme antialiased${isChatTab ? ' dashboard-chat-active' : ''}`

  useEffect(() => {
    previousMobileNavIndexRef.current = mobileNavActiveIndex
  }, [mobileNavActiveIndex])

  useEffect(() => {
    if (activeTab !== 'chat') {
      setIsChatMobileSidebarOpen(false)
    }
  }, [activeTab])

  function handleStartNewChat() {
    actions.startNewChat()
    if (activeTab !== 'chat') {
      setActiveTab('chat')
    }
  }

  function handleResetChats() {
    actions.resetChats()
    if (activeTab !== 'chat') {
      setActiveTab('chat')
    }
  }

  function handleDeleteChatThread(threadId: string) {
    const targetThread = chatThreads.find((thread) => thread.id === threadId)
    if (!targetThread) {
      return
    }

    const shouldDelete = window.confirm(`Delete "${targetThread.title}"? This cannot be undone.`)
    if (!shouldDelete) {
      return
    }

    actions.deleteChatThread(targetThread.id)
    if (activeTab !== 'chat') {
      setActiveTab('chat')
    }
  }

  function handleRenameChatThread(threadId: string) {
    const targetThread = chatThreads.find((thread) => thread.id === threadId)
    if (!targetThread) {
      return
    }

    const nextTitle = window.prompt('Rename chat', targetThread.title)
    if (nextTitle === null) {
      return
    }

    const normalizedTitle = nextTitle.trim()
    if (!normalizedTitle || normalizedTitle === targetThread.title) {
      return
    }

    actions.renameChatThread(targetThread.id, normalizedTitle)
    if (activeTab !== 'chat') {
      setActiveTab('chat')
    }
  }

  function renderActiveTab() {
    switch (activeTab) {
      case 'home':
        return (
          <HomeTab
            firstName={firstName}
            metrics={metrics}
            providers={providers}
            reports={reports}
            tasks={tasks}
          />
        )
      case 'chat':
        return (
          <ChatTab
            activeChatThreadId={activeChatThreadId}
            chatThreads={chatThreads}
            messages={chatMessages}
            chatLoading={chatLoading}
            isMobileSidebarOpen={isChatMobileSidebarOpen}
            onDeleteChatThread={handleDeleteChatThread}
            onMobileSidebarOpenChange={setIsChatMobileSidebarOpen}
            onRenameChatThread={handleRenameChatThread}
            onResetChats={handleResetChats}
            onSendMessage={actions.handleSendMessage}
            onStartNewChat={handleStartNewChat}
            onSwitchChatThread={actions.switchChatThread}
          />
        )
      case 'timeline':
        return <TimelineTab />
      case 'health':
        return (
          <HealthMetricsTab
            metrics={metrics}
            reports={reports}
            records={records}
            tasks={tasks}
            taskProgress={metrics.taskProgress}
          />
        )
      case 'sleep':
        return <SleepTab />
      case 'fitness':
        return <FitnessTab />
      case 'nutrition':
        return <NutritionTab />
      case 'symptoms':
        return <SymptomsTab />
      case 'longevity':
        return <LongevityTab />
      case 'tests':
        return <TestsTab />
      case 'records':
        return (
          <RecordsVaultTab
            onAddRecord={actions.handleAddRecord}
            onDeleteRecord={actions.handleDeleteRecord}
            records={records}
          />
        )
      case 'medications':
        return <MedicationsTab medications={medications} onAddMedication={actions.handleAddMedication} />
      case 'supplements':
        return <SupplementsTab />
      case 'immunizations':
        return <ImmunizationsTab />
      case 'reports':
        return (
          <ReportsTab
            accountEmail={member.email}
            memberName={member.name}
            medications={medications}
            personalProfile={personalProfile}
            providers={providers}
            records={records}
            reports={reports}
            onGenerateReport={actions.handleGenerateReport}
            onUpdateReportMetadata={actions.handleUpdateReportMetadata}
          />
        )
      case 'genetics':
        return <GeneticsTab />
      case 'goals':
        return <GoalsTab />
      case 'settings':
        return (
          <SettingsTab
            accountEmail={member.email}
            agentApiEnabled={agentApiEnabled}
            isConfigured={isOpenRouterConfigured}
            onClearSettings={actions.clearOpenRouterSettings}
            onDeleteAllChats={actions.clearAllChats}
            onSignOut={onLogout}
            onSavePersonalProfile={actions.savePersonalProfile}
            onResetLocalStorage={actions.resetLocalStorage}
            onThemePreferenceChange={onThemePreferenceChange}
            onSaveSettings={actions.saveOpenRouterSettings}
            personalProfile={personalProfile}
            settings={openRouterSettings}
            signOutLabel={authMode === 'mock' ? 'Switch account' : 'Sign out'}
            themePreference={themePreference}
          />
        )
      case 'providers':
        return (
          <ProvidersTab
            onAddCustomProvider={actions.handleAddCustomProvider}
            providers={providers}
            toggleProvider={actions.toggleProvider}
          />
        )
      case 'consult':
        return (
          <ConsultationsTab
            consultations={consultations}
            onBookConsultation={actions.handleBookConsultation}
          />
        )
      case 'devices':
        return <WearablesTab />
      case 'family':
        return <FamilyTab />
      case 'emergency':
        return <EmergencyTab />
      default:
        return null
    }
  }

  return (
    <div className={dashboardShellClassName}>
      <SidebarLayout
        navbar={({ openSidebar }) => (
          <nav className="dashboard-mobile-nav-root" aria-label="Mobile navigation">
            <svg className="dashboard-mobile-nav-filter" aria-hidden="true" focusable="false">
              <defs>
                <filter id="dashboard-mobile-nav-liquid" x="-20%" y="-20%" width="140%" height="140%">
                  <feTurbulence
                    type="fractalNoise"
                    baseFrequency="0.75"
                    numOctaves="1"
                    seed="7"
                    result="dashboard-mobile-nav-noise"
                  />
                  <feGaussianBlur in="SourceGraphic" stdDeviation="0.001" result="dashboard-mobile-nav-blur" />
                  <feDisplacementMap
                    in="dashboard-mobile-nav-blur"
                    in2="dashboard-mobile-nav-noise"
                    scale="9"
                    xChannelSelector="R"
                    yChannelSelector="G"
                  />
                </filter>
              </defs>
            </svg>
            <div className="dashboard-mobile-nav-dock">
              <div className="dashboard-mobile-switcher">
                <fieldset className="dashboard-mobile-nav-main" c-previous={String(mobileNavPreviousIndex)}>
                  <legend className="dashboard-mobile-switcher-legend">Primary navigation</legend>

                  <label className="dashboard-mobile-switcher-option" aria-label="Home">
                    <NativeInput
                      className="dashboard-mobile-switcher-input"
                      type="radio"
                      name="dashboard-mobile-main-nav"
                      c-option="1"
                      checked={isHomeTab}
                      onChange={() => setActiveTab('home')}
                    />
                    <HomeIcon className="dashboard-mobile-switcher-icon" />
                    <span className="dashboard-mobile-switcher-label">Home</span>
                  </label>

                  <label className="dashboard-mobile-switcher-option" aria-label="Chat">
                    <NativeInput
                      className="dashboard-mobile-switcher-input"
                      type="radio"
                      name="dashboard-mobile-main-nav"
                      c-option="2"
                      checked={isChatTab}
                      onChange={() => setActiveTab('chat')}
                    />
                    <ChatBubbleLeftRightIcon className="dashboard-mobile-switcher-icon" />
                    <span className="dashboard-mobile-switcher-label">Chat</span>
                  </label>

                  <label className="dashboard-mobile-switcher-option" aria-label="More">
                    <NativeInput
                      className="dashboard-mobile-switcher-input"
                      type="radio"
                      name="dashboard-mobile-main-nav"
                      c-option="3"
                      checked={isMoreTab}
                      onChange={openSidebar}
                    />
                    <Bars3Icon className="dashboard-mobile-switcher-icon" />
                    <span className="dashboard-mobile-switcher-label">More</span>
                  </label>
                </fieldset>
              </div>

              <div className="dashboard-mobile-nav-user">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="dashboard-user-menu-trigger dashboard-mobile-user-orb-trigger"
                      aria-label="User menu"
                    >
                      <UserCircleIcon className="dashboard-mobile-switcher-icon" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="top"
                    align="end"
                    sideOffset={6}
                    className={USER_MENU_CONTENT_CLASS}
                  >
                    <DropdownMenuItem className={USER_MENU_ITEM_CLASS} onClick={onBackHome}>
                      <HomeIcon data-slot="icon" />
                      <span className="truncate">Back to homepage</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem className={USER_MENU_ITEM_CLASS} onClick={onLogout}>
                      <ArrowRightOnRectangleIcon data-slot="icon" />
                      <span className="truncate">{authMode === 'mock' ? 'Switch account' : 'Sign out'}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className={USER_MENU_ITEM_CLASS} disabled>
                      <UserCircleIcon data-slot="icon" />
                      <span className="truncate">{member.email}</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </nav>
        )}
        sidebar={
          <Sidebar className="dashboard-sidebar">
            <SidebarBody className="dashboard-sidebar-body">
              {sidebarTabGroups.map((group, groupIndex) => (
                <SidebarSection key={groupIndex}>
                  {group.label && (
                    <p className="dashboard-sidebar-group-label">
                      {group.label}
                    </p>
                  )}
                  {group.tabs.map((tab) => {
                    const Icon = TAB_ICONS[tab.iconKey]
                    return (
                      <SidebarItem
                        key={tab.id}
                        current={activeTab === tab.id}
                        className="dashboard-sidebar-link"
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {Icon && <Icon data-slot="icon" />}
                        <SidebarLabel>{tab.label}</SidebarLabel>
                      </SidebarItem>
                    )
                  })}
                </SidebarSection>
              ))}

              <SidebarSpacer />
            </SidebarBody>

            <SidebarFooter className="dashboard-sidebar-footer">
              <Button
                type="button"
                variant="unstyled"
                className="dashboard-sidebar-profile flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left"
                onClick={() => {
                  if (hasSettingsTab) {
                    setActiveTab('settings')
                  }
                }}
                aria-label="Open user profile"
              >
                <Avatar initials={memberInitials} className="dashboard-profile-avatar size-8 text-white text-xs font-semibold" />
                <div className="min-w-0">
                  <p className={`truncate text-sm/5 font-semibold ${uiClasses.textPrimary}`}>{profileDisplayName}</p>
                  <p className={`truncate text-xs/4 ${uiClasses.textSubtle}`}>{member.email}</p>
                </div>
              </Button>
            </SidebarFooter>
          </Sidebar>
        }
      >
        <div className="dashboard-page-content min-w-0">
          <section className="dashboard-overview ui-dashboard-overview">
            <div className="dashboard-overview-heading-row">
              {isChatTab && (
                <Button
                  type="button"
                  variant="unstyled"
                  className={`dashboard-chat-mobile-toggle dashboard-overview-chat-toggle${isChatMobileSidebarOpen ? ' is-open' : ''}`}
                  onClick={() => setIsChatMobileSidebarOpen((currentValue) => !currentValue)}
                  aria-controls="dashboard-chat-sidebar-panel"
                  aria-expanded={isChatMobileSidebarOpen}
                  aria-label={isChatMobileSidebarOpen ? 'Hide conversations panel' : 'Show conversations panel'}
                  title={isChatMobileSidebarOpen ? 'Hide conversations' : 'Show conversations'}
                >
                  <Bars3Icon className="size-4" aria-hidden="true" />
                  <span className="sr-only">Conversations</span>
                </Button>
              )}
              <Heading className={`dashboard-overview-title text-xl/7 font-semibold tracking-[-0.018em] sm:text-2xl/8 ${uiClasses.textPrimary}`}>
                {headerTitle}
              </Heading>
            </div>
          </section>

          <div className="dashboard-content-center min-w-0">
            <section className="dashboard-content-shell min-w-0">
              {renderActiveTab()}
            </section>
          </div>
        </div>
      </SidebarLayout>
    </div>
  )
}
